/**
 * @fileoverview CheckpointDialog
 * @description Add or edit one checkpoint.
 *
 *              Every field in this dialog is rendered from the schema the
 *              backend publishes in `/catalog`, so a new checkpoint kind
 *              appears here without a frontend change. The dialog can also
 *              dry-run the checkpoint on its own: guessing an element name or
 *              a JSON path is the easiest way to add a checkpoint that
 *              silently reports zero crew, and a chain full of phantom
 *              mismatches is worse than no checkpoint at all.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton,
  MenuItem, Paper, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AlertTriangle, CheckCircle2, Eye, Info, KeyRound, Plus, Trash2, X,
} from 'lucide-react';
import { crewPipelineCheckApi } from '../services/crewPipelineCheckApi';
import { RawResponseButton } from './RawResponseViewer';

const TEAL = '#0097A9';
const NAVY = '#01426A';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: '#fff',
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(0,151,169,0.10)' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: TEAL },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: TEAL, borderWidth: 2,
  },
};

const monoSx = {
  ...inputSx,
  '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.78rem' },
};

const slug = (value) =>
  String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(?:^-)|(?:-$)/g, '').slice(0, 40);

/* ── Key/value editor for headers and query parameters ── */
function KeyValueEditor({ label, help, value, onChange }) {
  const rows = useMemo(() => Object.entries(value || {}), [value]);

  const update = (index, field, next) => {
    const entries = rows.map(([k, v], i) =>
      i === index ? (field === 'key' ? [next, v] : [k, next]) : [k, v],
    );
    onChange(Object.fromEntries(entries.filter(([k]) => k !== '' || entries.length === 1)));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
        <Typography variant="caption" fontWeight={700} color={NAVY}>{label}</Typography>
        <Button
          size="small" startIcon={<Plus size={13} />}
          onClick={() => onChange({ ...(value || {}), '': '' })}
          sx={{ textTransform: 'none', fontWeight: 600, color: TEAL, fontSize: '0.72rem' }}
        >
          Add
        </Button>
      </Stack>
      <Stack spacing={1}>
        {rows.map(([key, val], index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <TextField
              size="small" placeholder="name" value={key} sx={{ ...monoSx, flex: 1 }}
              onChange={(e) => update(index, 'key', e.target.value)}
            />
            <TextField
              size="small" placeholder="value or {token}" value={val} sx={{ ...monoSx, flex: 1.4 }}
              onChange={(e) => update(index, 'value', e.target.value)}
            />
            <Tooltip title="Remove">
              <IconButton
                size="small"
                onClick={() => onChange(Object.fromEntries(rows.filter((_, i) => i !== index)))}
                sx={{ color: '#dc2626' }}
              >
                <Trash2 size={14} />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
        {!rows.length && (
          <Typography variant="caption" color="text.secondary">None yet.</Typography>
        )}
      </Stack>
      {help && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{help}</Typography>
      )}
    </Box>
  );
}

/* ── One config/mapping field, rendered from its schema entry ── */
function SchemaField({ field, value, onChange }) {
  const common = {
    size: 'small',
    fullWidth: true,
    label: field.label,
    helperText: field.help,
    required: field.required,
  };

  switch (field.type) {
    case 'boolean':
      return (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              sx={{ '& .Mui-checked': { color: TEAL }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: TEAL } }}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>{field.label}</Typography>
              {field.help && <Typography variant="caption" color="text.secondary">{field.help}</Typography>}
            </Box>
          }
        />
      );
    case 'number':
      return (
        <TextField
          {...common} type="number" sx={inputSx}
          value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'select':
      return (
        <TextField
          {...common} select sx={inputSx}
          value={value || field.options?.[0] || ''} onChange={(e) => onChange(e.target.value)}
        >
          {(field.options || []).map((option) => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </TextField>
      );
    case 'xml':
    case 'sql':
    case 'textarea':
      return (
        <TextField
          {...common} multiline minRows={field.type === 'textarea' ? 3 : 6} sx={monoSx}
          value={value || ''} onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'tokenlist':
      return (
        <TextField
          {...common} sx={monoSx}
          placeholder="{flight}, {date}, {station}"
          value={Array.isArray(value) ? value.join(', ') : value || ''}
          onChange={(e) =>
            onChange(e.target.value.split(',').map((v) => v.trim()).filter(Boolean))
          }
        />
      );
    case 'keyvalue':
      return <KeyValueEditor label={field.label} help={field.help} value={value} onChange={onChange} />;
    default:
      return (
        <TextField
          {...common} sx={field.type === 'url' ? monoSx : inputSx}
          value={value || ''} onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

/* ── One crew-field mapping, with its validation state ──
   A path that does not exist resolves to an empty string rather than an
   error, so the only honest signal is how many records it actually filled.
   The picker is backed by the paths the last dry run really saw. */
function MappedField({ field, value, options, report, onChange }) {
  const state = report
    ? report.resolved === 0
      ? 'empty'
      : report.resolved < report.total
        ? 'partial'
        : 'ok'
    : 'unknown';

  const colour = { ok: '#00875A', partial: '#b45309', empty: '#dc2626', unknown: '#94a3b8' }[state];
  const note = {
    ok: `resolved on all ${report?.total ?? 0}`,
    partial: `resolved on ${report?.resolved}/${report?.total}`,
    empty: 'resolved on none — check the path',
    unknown: field.required ? 'required' : 'optional',
  }[state];

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      value={value || ''}
      onChange={(_, next) =>
        onChange(next == null ? '' : typeof next === 'string' ? next : next.path)
      }
      onInputChange={(_, next, reason) => { if (reason === 'input') onChange(next); }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.path || '')}
      filterOptions={(opts, params) => {
        const query = params.inputValue.split(/[|+]/).pop().trim().toLowerCase();
        const matched = query
          ? opts.filter(
              (o) =>
                o.path.toLowerCase().includes(query) ||
                String(o.sample ?? '').toLowerCase().includes(query),
            )
          : opts;
        return matched.slice(0, 50);
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.path} sx={{ display: 'block !important', py: 0.6 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.74rem', color: '#0f172a' }}>
            {option.path}
          </Typography>
          {option.sample !== '' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              e.g. {option.sample}
            </Typography>
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={monoSx}
          label={field.label}
          required={field.required}
          helperText={
            <Box component="span" sx={{ color: colour, fontWeight: 600 }}>
              {note}
              {report?.sample ? ` · e.g. ${report.sample}` : ''}
            </Box>
          }
        />
      )}
    />
  );
}

const CheckpointDialog = ({
  open, onClose, onSave, checkpoint, schema, crewFields, tokens, runParams, flowId,
}) => {
  const [draft, setDraft] = useState(checkpoint);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState('');
  const [tokensOpen, setTokensOpen] = useState(false);

  useEffect(() => {
    setDraft(checkpoint);
    setTestResult(null);
    setTestError('');
  }, [checkpoint]);

  /* Dry-run findings, keyed for the mapping rows. Cleared whenever the
     connection changes, because stale findings are worse than none. */
  const availableFields = testResult?.availableFields || [];
  const fieldReports = useMemo(
    () => Object.fromEntries((testResult?.fieldReport || []).map((r) => [r.field, r])),
    [testResult],
  );

  const setField = useCallback((section, key, value) => {
    setDraft((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: value } }));
  }, []);

  const setCrewField = useCallback((key, value) => {
    setDraft((prev) => ({
      ...prev,
      mapping: { ...(prev.mapping || {}), fields: { ...(prev.mapping?.fields || {}), [key]: value } },
    }));
  }, []);

  const runTest = useCallback(async () => {
    setTesting(true);
    setTestError('');
    setTestResult(null);
    try {
      const res = await crewPipelineCheckApi.testCheckpoint({
        ...runParams,
        flow_id: flowId,
        checkpoint: draft,
      });
      setTestResult(res.result);
    } catch (err) {
      setTestError(err.message || 'The dry run failed.');
    } finally {
      setTesting(false);
    }
  }, [draft, runParams, flowId]);

  if (!draft || !schema) return null;

  const isNew = !checkpoint?.id;
  const canSave =
    Boolean(draft.label?.trim()) &&
    Boolean(draft.short?.trim()) &&
    ['crew_id', 'name', 'position'].every((key) => draft.mapping?.fields?.[key]?.trim());

  const mappingGaps = (testResult?.fieldReport || [])
    .filter((report) => report.resolved === 0)
    .map((report) => report.field);

  const handleSave = () =>
    onSave({
      ...draft,
      id: draft.id || slug(draft.label),
      label: draft.label.trim(),
      short: draft.short.trim().toUpperCase().slice(0, 6),
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight={800} color={NAVY}>
              {isNew ? 'Add checkpoint' : `Edit ${checkpoint.label}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">{schema.description}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><X size={18} /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#fbfdfe' }}>
        <Stack spacing={2.5}>
          {/* Identity */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small" fullWidth required label="Name" sx={inputSx}
              value={draft.label || ''}
              helperText="Shown on the progress rail and in the report."
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  label: e.target.value,
                  short: prev.short || e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 4).toUpperCase(),
                }))
              }
            />
            <TextField
              size="small" required label="Short code" sx={{ ...inputSx, maxWidth: { sm: 160 } }}
              value={draft.short || ''}
              inputProps={{ maxLength: 6 }}
              helperText="Column heading, e.g. AIMS"
              onChange={(e) => setDraft((prev) => ({ ...prev, short: e.target.value.toUpperCase() }))}
            />
            <TextField
              size="small" select label="Cost" sx={{ ...inputSx, maxWidth: { sm: 170 } }}
              value={draft.weight || 2}
              helperText="Share of the progress bar"
              onChange={(e) => setDraft((prev) => ({ ...prev, weight: Number(e.target.value) }))}
            >
              <MenuItem value={1}>Fast</MenuItem>
              <MenuItem value={2}>Typical</MenuItem>
              <MenuItem value={3}>Slow</MenuItem>
              <MenuItem value={5}>Very slow</MenuItem>
            </TextField>
          </Stack>

          {/* Token reference */}
          <Box>
            <Button
              size="small" startIcon={<Info size={14} />} onClick={() => setTokensOpen((o) => !o)}
              sx={{ textTransform: 'none', fontWeight: 600, color: TEAL, fontSize: '0.75rem', px: 0.5 }}
              aria-expanded={tokensOpen}
            >
              Available tokens
            </Button>
            <Collapse in={tokensOpen}>
              <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.75}>
                {(tokens || []).map((token) => (
                  <Tooltip key={token.token} title={token.description}>
                    <Chip
                      size="small" label={token.token}
                      sx={{ fontFamily: 'monospace', fontSize: '0.68rem', bgcolor: '#eff6ff', color: NAVY }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Collapse>
          </Box>

          {/* Connection */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <Typography variant="subtitle2" fontWeight={800} color={NAVY} mb={1.5}>Connection</Typography>
            <Stack spacing={2}>
              {schema.config.map((field) => (
                <SchemaField
                  key={field.key}
                  field={field}
                  value={draft.config?.[field.key]}
                  onChange={(value) => setField('config', field.key, value)}
                />
              ))}
            </Stack>
          </Paper>

          {/* Credentials */}
          {Boolean(schema.secrets?.length) && (
            <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <KeyRound size={15} color={NAVY} />
                <Typography variant="subtitle2" fontWeight={800} color={NAVY}>Credentials</Typography>
              </Stack>
              <Stack spacing={2}>
                {schema.secrets.map((secret) => {
                  const alreadySet = draft.secretsSet?.[secret.key];
                  return (
                    <TextField
                      key={secret.key}
                      size="small" fullWidth type="password" sx={inputSx}
                      label={secret.label}
                      value={draft.secrets?.[secret.key] || ''}
                      placeholder={alreadySet ? 'Saved — leave blank to keep' : ''}
                      helperText={
                        alreadySet
                          ? `${secret.help} Already saved; leave blank to keep the stored value.`
                          : secret.help
                      }
                      onChange={(e) => setField('secrets', secret.key, e.target.value)}
                    />
                  );
                })}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                Credentials are encrypted before they are stored and are never sent back to the browser.
                Saved flows are shared, so a credential you add here is usable by anyone with access
                to this module.
              </Typography>
            </Paper>
          )}

          {/* Mapping */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box>
                <Typography variant="subtitle2" fontWeight={800} color={NAVY}>Where the crew is</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {schema.fieldHelp}
                </Typography>
              </Box>
              <Button
                size="small" variant="outlined"
                startIcon={testing ? <CircularProgress size={13} color="inherit" /> : <Eye size={14} />}
                onClick={runTest}
                disabled={testing || !runParams?.flight || !runParams?.station}
                sx={{
                  borderColor: NAVY, color: NAVY, borderRadius: '8px', flexShrink: 0,
                  textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap',
                }}
              >
                {testing ? 'Checking…' : 'Validate mapping'}
              </Button>
            </Stack>

            {testError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: '10px' }}>{testError}</Alert>}
            {testResult?.error && (
              <Alert
                severity="warning"
                icon={<AlertTriangle size={16} />}
                sx={{ mt: 1.5, borderRadius: '10px' }}
                action={<RawResponseButton raw={testResult.raw} title={draft.label} detail={testResult.detail} />}
              >
                {testResult.error}
              </Alert>
            )}
            {testResult && !testResult.error && (
              <Alert
                severity={mappingGaps.length ? 'warning' : 'success'}
                icon={mappingGaps.length ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
                sx={{ mt: 1.5, borderRadius: '10px' }}
                action={<RawResponseButton raw={testResult.raw} title={draft.label} detail={testResult.detail} />}
              >
                Read {testResult.crewCount} record{testResult.crewCount === 1 ? '' : 's'} in{' '}
                {testResult.durationMs}ms.{' '}
                {mappingGaps.length
                  ? `${mappingGaps.join(', ')} resolved on no record — check the raw response before trusting those paths.`
                  : 'Every mapped field resolved.'}
              </Alert>
            )}

            <Stack spacing={2} mt={2}>
              {schema.mapping.map((field) => (
                <SchemaField
                  key={field.key}
                  field={field}
                  value={draft.mapping?.[field.key]}
                  onChange={(value) => setField('mapping', field.key, value)}
                />
              ))}
              <Divider>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  FIELD MAPPING
                </Typography>
              </Divider>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                }}
              >
                {(crewFields || []).map((field) => (
                  <MappedField
                    key={field.key}
                    field={field}
                    value={draft.mapping?.fields?.[field.key] || ''}
                    options={availableFields}
                    report={fieldReports[field.key]}
                    onChange={(value) => setCrewField(field.key, value)}
                  />
                ))}
              </Box>
              {!availableFields.length && (
                <Typography variant="caption" color="text.secondary">
                  Run <strong>Validate mapping</strong> to load the fields this source actually returns
                  and check every path against them.
                </Typography>
              )}
            </Stack>
          </Paper>

          {/* Sample of what came back */}
          {testResult && !testResult.error && Boolean(testResult.crew?.length) && (
            <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <Typography variant="subtitle2" fontWeight={800} color={NAVY} mb={1}>
                What this checkpoint would contribute
              </Typography>
              <Box
                sx={{
                  maxHeight: 180, overflowY: 'auto',
                  border: '1px solid #e5e7eb', borderRadius: '10px', bgcolor: '#f8fafc', p: 1.25,
                }}
              >
                {testResult.crew.slice(0, 25).map((member, index) => (
                  <Typography
                    key={index}
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#475569', whiteSpace: 'pre' }}
                  >
                    {(member.position || '??').padEnd(4)} {member.name || '(unnamed)'} ({member.crewId || '?'})
                  </Typography>
                ))}
              </Box>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
          Cancel
        </Button>
        <Button
          variant="contained" onClick={handleSave} disabled={!canSave}
          sx={{
            bgcolor: TEAL, borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3,
            '&:hover': { bgcolor: '#007f8f' },
          }}
        >
          {isNew ? 'Add checkpoint' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckpointDialog;
